
import Vue from 'vue';
import { storeGet } from '@/store/store-utils';
function getNova() {
  return Vue.prototype.$nova;
}

/**
 * Returns CSV table headers with dynamic labels
 * @returns {Array} - Array of table header objects
 */
function getCsvTableHeaders() {
  const $nova = getNova();
  const ruLabel = $nova.labelsName('access_management', 'commentary_ru_code_label', 'RU Code');
  return [
    { text: '#', value: 'id', sortable: false, class: 'tableHeaderBg' },
    { text: ruLabel, value: 'reportingUnits', sortable: false, class: 'tableHeaderBg' },
    { text: 'Email', value: 'email', sortable: true, class: 'tableHeaderBg' },
    { text: 'Error', value: 'error', sortable: false, class: 'tableHeaderBg error--text' }
  ];
}

export default {
  namespaced: true,

  state: {
    // Bulk upload state
    uploadData: [],
    file: [],
    csvHeaders: [],
    csvData: [],
    showDataWithError: false,
    bulkUploadLoading: false,
    assignedReportingUnits: [],
    unassignedReportingUnits: [],
    // Main User Permission List
    userPermissionsList: [],
    userPermissionsRoles: [],
    userPermissionsSummary: {
      active_users: 0,
      inactive_users: 0,
      total_users: 0
    },
    userPermissionsLastFetched: null,
    // Caching context for details
    lastFetchedUserId: null,
    assignedRUCacheTime: null,
    unassignedRUCacheTime: null
  },

  getters: {
    getAssignedReportingUnits: (state) => state.assignedReportingUnits,
    getUnassignedReportingUnits: (state) => state.unassignedReportingUnits,
    getUserPermissionsList: (state) => state.userPermissionsList,
    getUserPermissionsRoles: (state) => state.userPermissionsRoles,
    getUserPermissionsSummary: (state) => state.userPermissionsSummary,
    getUploadData: (state) => state.uploadData,
    getFile: (state) => state.file,
    getCsvHeaders: (state) => state.csvHeaders,
    getCsvData: (state) => state.csvData,
    getFilteredCsvData: (state) => {
      if (state.showDataWithError) {
        return state.csvData.filter(item => item.hasError);
      }
      return state.csvData;
    },
    getShowDataWithError: (state) => state.showDataWithError,
    hasValidationErrors: (state) => state.csvData.some(item => item.hasError),
    getBulkUploadLoading: (state) => state.bulkUploadLoading
  },

  mutations: {
    SET_ASSIGNED_REPORTING_UNITS(state, data) {
      state.assignedReportingUnits = data;
      state.assignedRUCacheTime = Date.now();
    },
    SET_UNASSIGNED_REPORTING_UNITS(state, data) {
      state.unassignedReportingUnits = data;
      state.unassignedRUCacheTime = Date.now();
    },
    SET_LAST_FETCHED_USER_ID(state, userId) {
      if (state.lastFetchedUserId !== userId) {
          // Invalidate caches if user changes
          state.assignedReportingUnits = [];
          state.unassignedReportingUnits = [];
          state.assignedRUCacheTime = null;
          state.unassignedRUCacheTime = null;
      }
      state.lastFetchedUserId = userId;
    },

    SET_USER_PERMISSIONS_DATA(state, { data, roles, summary }) {
      state.userPermissionsList = data;
      state.userPermissionsRoles = roles;
      state.userPermissionsSummary = summary;
      state.userPermissionsLastFetched = Date.now();
    },

    SET_UPLOAD_DATA(state, data) {
      state.uploadData = data;
    },

    SET_FILE(state, file) {
      state.file = file;
    },

    SET_CSV_HEADERS(state, headers) {
      state.csvHeaders = headers;
    },

    SET_CSV_DATA(state, data) {
      state.csvData = data;
    },

    SET_SHOW_DATA_WITH_ERROR(state, value) {
      state.showDataWithError = value;
    },

    SET_BULK_UPLOAD_LOADING(state, value) {
      state.bulkUploadLoading = value;
    },

    RESET_UPLOAD_DATA(state) {
      state.uploadData = [];
      state.file = [];
      state.csvData = [];
      state.csvHeaders = [];
      state.showDataWithError = false;
    }
  },

  actions: {
    async fetchUserPermissions({ commit, state, rootState }, { axiosInstance, force = false }) {
        // const CACHE_duration = 5 * 60 * 1000; // 5 minutes
        // const now = Date.now();
        
        if (!force && state.userPermissionsList.length > 0 && state.userPermissionsLastFetched) {
            return; // Use cache indefinitely until forced refresh
        }
        
        try {
            const response = await storeGet(axiosInstance, rootState, "/v2/varianceanalysis/userpermission/getdata");
            if (response.data && response.data.status === "success") {
                const rawData = response.data.data[0] || {};
                commit('SET_USER_PERMISSIONS_DATA', {
                    data: rawData.data || [],
                    roles: rawData.roles || [],
                    summary: rawData.summary || { active_users: 0, inactive_users: 0, total_users: 0 }
                });
            } else {
                throw new Error("Failed to fetch user permissions data");
            }
        } catch (error) {
            console.error('Error fetching user permissions:', error);
            throw error;
        }
    },

    async fetchAssignedReportingUnits({ commit, state, rootState }, { userId, filter, client, axiosInstance, force = false }) {
      if (!userId) {
        throw new Error('User ID is required to fetch assigned reporting units');
      }

      // Check for user switch or cache validity
      // const CACHE_DURATION = 5 * 60 * 1000;
      // const now = Date.now();
      const isSameUser = state.lastFetchedUserId === userId;
      
      if (!isSameUser) {
          commit('SET_LAST_FETCHED_USER_ID', userId);
      }

      if (isSameUser && !force && state.assignedRUCacheTime) {
          return; // Use cache
      }

      try {
        const params = { cid: client, ...filter };
        const response = await storeGet(
          axiosInstance, rootState,
          `/v2/varianceanalysis/userpermission/get-reporting-units/assigned/${encodeURIComponent(userId)}`,
          params
        );

        if (response?.data?.status === 'success') {
          commit('SET_ASSIGNED_REPORTING_UNITS', response.data.data);
          return response.data;
        } else {
          throw new Error(response?.data?.message || 'Failed to fetch assigned reporting units');
        }
      } catch (error) {
        console.error('Error fetching assigned reporting units:', error);
        throw error;
      }
    },

    async fetchUnassignedReportingUnits({ commit, state, rootState }, { userId, filter, client, axiosInstance, force = false }) {
      if (!userId) {
        throw new Error('User ID is required to fetch unassigned reporting units');
      }
      
      // const CACHE_DURATION = 5 * 60 * 1000;
      // const now = Date.now();
      const isSameUser = state.lastFetchedUserId === userId;

      if (!isSameUser) {
           commit('SET_LAST_FETCHED_USER_ID', userId);
      }

      if (isSameUser && !force && state.unassignedRUCacheTime) {
          return;
      }

      try {
        const params = { cid: client, ...filter };
        const response = await storeGet(
          axiosInstance, rootState,
          `/v2/varianceanalysis/userpermission/get-reporting-units/unassigned/${encodeURIComponent(userId)}`,
          params
        );

        if (response?.data?.status === 'success') {
          // Store the full response object to preserve pagination metadata
          commit('SET_UNASSIGNED_REPORTING_UNITS', response.data);
          return response.data;
        } else {
          throw new Error(response?.data?.message || 'Failed to fetch unassigned reporting units');
        }
      } catch (error) {
        console.error('Error fetching unassigned reporting units:', error);
        throw error;
      }
    },

    async processFilesBulk({ commit, dispatch }, files) {
      commit('SET_FILE', files);

      if (files && files.length > 0) {
        const file = files[0];
        const isValidCsv = file.type === 'text/csv' || file.name.endsWith('.csv');

        if (isValidCsv) {
          const parseSuccess = await dispatch('parseCSVFile', file);
          if (parseSuccess) {
            commit('SET_UPLOAD_DATA', [{ name: file.name, status: 'Ready to upload' }]);
            return true;
          }
          return false;
        } else {
          commit('sbError', 'Please upload a valid CSV file', { root: true });
          return false;
        }
      }
      return false;
    },

    async parseCSVFile({ commit, dispatch }, file) {
      try {
        const $nova = getNova();
        const dt = await $nova.readCSV(file, { removeblankrows: true });

        // Need at least 2 rows (1 header + 1 data row)
        if (!dt || dt.length < 2) {
          commit('sbError', 'CSV file is empty or has no valid data', { root: true });
          dispatch('resetUploadData');
          return false;
        }

        // Set headers for the table with dynamic labels
        commit('SET_CSV_HEADERS', getCsvTableHeaders());

        // Skip first row (header row) and parse data rows
        const dataRows = dt.slice(1);
        const parsedData = [];
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        // Get dynamic label for RU Code
        const ruLabel = $nova.labelsName('access_management', 'commentary_ru_code_label', 'RU Code');

        dataRows.forEach((row, index) => {
          const reportingUnits = (row[0] || '').trim();
          const email = (row[1] || '').trim();
          const errors = [];

          // Validate empty reporting units
          if (!reportingUnits) {
            errors.push(`${ruLabel} is required`);
          }

          // Validate empty email
          if (!email) {
            errors.push('Email is required');
          } else if (!emailRegex.test(email) || email.includes(',') || email.endsWith('.')) {
            // Validate email format
            errors.push('Invalid email format');
          }

          parsedData.push({
            id: index + 1,
            email: email,
            reportingUnits: reportingUnits,
            error: errors.join(', '),
            hasError: errors.length > 0
          });
        });

        commit('SET_CSV_DATA', parsedData);
        return true;
      } catch (err) {
        commit('sbError', err.message || 'Failed to parse CSV file', { root: true });
        dispatch('resetUploadData');
        console.log('[UserPermission Store]:', err);
        return false;
      }
    },

    resetUploadData({ commit }) {
      commit('RESET_UPLOAD_DATA');
    },

    setShowDataWithError({ commit }, value) {
      commit('SET_SHOW_DATA_WITH_ERROR', value);
    },

    async uploadBulkUsers({ commit, state, dispatch }, { axios, clientId }) {
      // Basic validation
      if (!state.file || !state.file.length) {
        commit('sbError', 'Please select a file to upload', { root: true });
        return { success: false, message: 'No file selected' };
      }

      commit('SET_BULK_UPLOAD_LOADING', true);

      try {
        const $nova = getNova();
        if (!$nova || typeof $nova.uploadFile !== 'function') {
          throw new Error('Upload service is unavailable');
        }

        // Upload file (defensive access)
        const file = state.file[0];
        const uploadResponse = await $nova.uploadFile(axios, file, {});

        // Validate upload response shape
        const uploadData = uploadResponse?.data;
        if (!uploadData || uploadData.status !== 'success' || !Array.isArray(uploadData.data) || !uploadData.data.length) {
          const message = uploadData?.message || 'File upload failed';
          throw new Error(message);
        }

        const fileId = uploadData.data[0]._id;
        if (!fileId) {
          throw new Error('Uploaded file ID missing in response');
        }

        // Prepare payload and call server API
        const payload = {
          data: { upload: fileId },
          client: clientId
        };

        const response = await axios.post('/v2/varianceanalysis/userpermission/fileUploadUserPermission', payload);

        // Validate API response shape
        const responseData = response?.data;
        if (!responseData) {
          throw new Error('Empty response from user-permission endpoint');
        }

        if (responseData.status === 'success') {
          commit('sbSuccess', 'User permissions uploaded successfully', { root: true });
          await dispatch('resetUploadData');
          return { success: true, message: 'Uploaded and processed successfully' };
        }

        // Non-success but valid response: surface backend message if any
        throw new Error(responseData.message || 'Failed to upload user permissions');

      } catch (err) {
        // Extract best-possible error message (axios-aware)
        const axiosMsg = err?.response?.data?.message;
        const statusCode = err?.response?.status;
        const networkError = err?.request && !err?.response ? 'Network error or no response received' : null;

        const message =
          axiosMsg ||
          (networkError ? networkError : null) ||
          err?.message ||
          'An unknown error occurred during upload';

        // User-facing notification
        commit('sbError', message, { root: true });

        // Dev logging (keeps full details)
        console.error('[UserPermission Store] uploadBulkUsers error:', {
          message,
          originalError: err,
          statusCode
        });

        return { success: false, message };
      } finally {
        commit('SET_BULK_UPLOAD_LOADING', false);
      }
    },

    async removeUserAccess({ rootState }, { userId, reportingUnitIds, client, axiosInstance }) {
      const effectiveClient = client || rootState.client;
      
      if (!userId || !reportingUnitIds || !effectiveClient) {
        throw new Error(`Missing required parameters: userId (${userId}), reportingUnitIds (${reportingUnitIds?.length}), or client (${effectiveClient})`);
      }

      try {
        const payload = {
          client: effectiveClient,
          data: {
            userId,
            reportingUnitIds
          }
        };

        const response = await axiosInstance.post(
          '/v2/varianceanalysis/userpermission/remove-user-access',
          payload
        );

        if (response?.data?.status === 'success') {
          return { success: true, message: response.data.message || 'User Access revoked successfully' };
        } else {
          throw new Error(response?.data?.message || 'Failed to revoke user access');
        }
      } catch (error) {
        console.error('Error removing user access:', error);
        throw error;
      }
    },

    async addUserAccess({ rootState }, { userId, reportingUnitIds, client, axiosInstance }) {
      const effectiveClient = client || rootState.client;
      
      if (!userId || !reportingUnitIds || !effectiveClient) {
        throw new Error(`Missing required parameters: userId (${userId}), reportingUnitIds (${reportingUnitIds?.length}), or client (${effectiveClient})`);
      }

      try {
        const payload = {
          client: effectiveClient,
          data: {
            userId,
            reportingUnitIds
          }
        };

        const response = await axiosInstance.post(
          '/v2/varianceanalysis/userpermission/add-user-access',
          payload
        );

        if (response?.data?.status === 'success') {
          return { success: true, message: response.data.message || 'User Access updated successfully' };
        } else {
          throw new Error(response?.data?.message || 'Failed to update user access');
        }
      } catch (error) {
        console.error('Error adding user access:', error);
        throw error;
      }
    }
  }
};
